import SendIcon from "@mui/icons-material/Send";
import Box from "@mui/material/Box";
import Paper from "@mui/material/Paper";
import Typography from "@mui/material/Typography";
import { styled } from "@mui/material/styles";
import LoadingButton from "_shared/_views/loading/loading-button";
import EmailAddressInput from "_shared/email-addresses/_views/email-address-input";
import EmailAddress from "_shared/email-addresses/email-address";
import NameInput from "_shared/names/_views/name-input";
import Name from "_shared/names/name";
import EntityNameInput from "legal-entities/entity-names/_views/inputs/entity-name-input";
import EntityName from "legal-entities/entity-names/entity-name";
import RegistrationInfo, {
  InvalidRegistrationInfoError,
} from "legal-entities/registration-info/registration-info";
import { RegistrationInfoParams } from "legal-entities/registration-info/_schema/registration-info-schema";
import InvitationMessageInput from "legal-entities/invitations/messages/_views/inputs/invitation-message-input";
import InvitationMessage from "legal-entities/invitations/messages/invitation-message";
import EntityOfficerInvitationAPIService from "legal-entities/invitations/officer-invitations/_api/entity-officer-invitation-api-service";
import EntityOfficerInvitation, {
  InvalidEntityOfficerInvitationError,
} from "legal-entities/invitations/officer-invitations/entity-officer-invitation";
import { EntityOfficerInvitationParams } from "legal-entities/invitations/officer-invitations/_schema/entity-officer-invitation-schema";
import { enqueueSnackbar } from "notistack";
import { useState } from "react";
import { useSession } from "users/__session/session-context";

const OfficerInvitationFormContainer = styled(Paper)(({ theme }) => ({
  [theme.breakpoints.down("md")]: {
    padding: theme.spacing(2),
  },
  padding: theme.spacing(6),
  position: "relative",
  width: "100%",
}));
const OfficerInvitationFields = styled(Box)(({ theme }) => ({
  maxWidth: theme.spacing(30),
}));
const InvitationFormTitle = styled(Typography)(({ theme }) => ({
  marginBottom: theme.spacing(1.5),
}));

type InvitationFormErrors = {
  general?: string;
  email?: string;
  name?: string;
  entityName?: string;
  message?: string;
};

type EntityOfficerInvitationFormProps = {
  className?: string;
  onInvitationCreated: (invitation: EntityOfficerInvitation) => void;
};

export default function EntityOfficerInvitationForm(
  props: Readonly<EntityOfficerInvitationFormProps>,
) {
  const { className, onInvitationCreated } = props;

  const session = useSession();

  const [sending, setSending] = useState<boolean>(false);

  const [inviteeEmail, setInviteeEmail] = useState<EmailAddress>();
  const [inviteeEntityName, setInviteeEntityName] = useState<EntityName>();
  const [inviteeName, setInviteeName] = useState<Name>();
  const [invitationMessage, setInvitationMessage] =
    useState<InvitationMessage>();
  const [formErrors, setFormErrors] = useState<InvitationFormErrors>();

  function formIsValid() {
    try {
      validateFormFields(getInvitationParamsFromState());
      return true;
    } catch {
      return false;
    }
  }
  function validateFormFields(
    formFields: Partial<
      Omit<
        RegistrationInfoParams & EntityOfficerInvitationParams,
        "registrationInfo"
      >
    >,
  ): asserts formFields is Omit<
    RegistrationInfoParams & EntityOfficerInvitationParams,
    "registrationInfo"
  > {
    const missingFields: string[] = [];
    if (!formFields.email) missingFields.push("Email Address");
    else if (!(formFields.email instanceof EmailAddress))
      throw new Error("Email Address is invalid type");

    if (!formFields.name) missingFields.push("Name");
    else if (!(formFields.name instanceof Name))
      throw new Error("Name is invalid type");

    if (!formFields.entityName) missingFields.push("Entity Name");
    else if (!(formFields.entityName instanceof EntityName))
      throw new Error("Entity Name is invalid type");

    if (missingFields.length > 0) {
      throw new InvalidEntityOfficerInvitationError(
        `Missing required fields: ${missingFields.join(", ")}`,
      );
    }
  }

  function updateFormError(type: keyof InvitationFormErrors, error?: string) {
    if (!error) return;
    let errors: InvitationFormErrors = { ...formErrors };
    errors[type] = error;
    setFormErrors(errors);
  }

  function getInvitationParamsFromState(): Partial<
    Omit<
      RegistrationInfoParams & EntityOfficerInvitationParams,
      "registrationInfo"
    >
  > {
    return {
      email: inviteeEmail,
      name: inviteeName,
      entityName: inviteeEntityName,
      message: invitationMessage,
    };
  }

  function buildInvitation(
    params: Partial<
      Omit<
        RegistrationInfoParams & EntityOfficerInvitationParams,
        "registrationInfo"
      >
    >,
  ): EntityOfficerInvitation | undefined {
    try {
      validateFormFields(params);
      return EntityOfficerInvitation.create({
        params: {
          ...params,
          registrationInfo: RegistrationInfo.create({
            params,
            options: { silenceSchemaErrors: true },
          }),
        },
        options: { silenceSchemaErrors: true },
      });
    } catch (error) {
      if (
        error instanceof InvalidEntityOfficerInvitationError ||
        error instanceof InvalidRegistrationInfoError
      ) {
        setFormErrors({ general: error.message });
        return;
      }
      throw error;
    }
  }

  async function sendInvitation() {
    setSending(true);

    try {
      const builtInvitation = buildInvitation(getInvitationParamsFromState());
      if (!builtInvitation)
        throw new InvalidEntityOfficerInvitationError("Invitation is invalid");
      const service = new EntityOfficerInvitationAPIService(session);
      const updatedInvitation =
        await service.inviteEntityOfficer(builtInvitation);
      enqueueSnackbar("Sent entity officer invitation", { variant: "success" });
      onInvitationCreated(updatedInvitation);
    } catch (ex) {
      console.error(ex);
      enqueueSnackbar("Failed to send entity officer invitation", {
        variant: "error",
      });
    } finally {
      clearForm();
      setSending(false);
    }
  }

  function clearForm() {
    setInviteeEmail(undefined);
    setInviteeEntityName(undefined);
    setInviteeName(undefined);
    setInvitationMessage(undefined);
  }

  return (
    <OfficerInvitationFormContainer className={className}>
      <InvitationFormTitle variant="h6">
        Invite Entity Officer
      </InvitationFormTitle>
      <OfficerInvitationFields>
        <EmailAddressInput
          idPrefix="officer-invitation"
          emailAddress={inviteeEmail}
          onChange={(updatedEmail?: EmailAddress, error?: string) => {
            if (updatedEmail) {
              setInviteeEmail(updatedEmail);
              buildInvitation({
                ...getInvitationParamsFromState(),
                email: updatedEmail,
              });
            }
            updateFormError("email", error);
          }}
        />
        <NameInput
          idPrefix="officer-invitation"
          name={inviteeName}
          onChange={(updatedName?: Name | undefined, error?: string) => {
            setInviteeName(updatedName);
            updateFormError("name", error);

            if (updatedName && !error) {
              buildInvitation({
                ...getInvitationParamsFromState(),
                name: updatedName,
              });
            }
          }}
        />
        <EntityNameInput
          idPrefix="officer-invitation"
          entityName={inviteeEntityName}
          onChange={(
            updatedEntityName?: EntityName | undefined,
            error?: string,
          ) => {
            setInviteeEntityName(updatedEntityName);
            updateFormError("entityName", error);

            if (updatedEntityName && !error) {
              buildInvitation({
                ...getInvitationParamsFromState(),
                entityName: updatedEntityName,
              });
            }
          }}
        />
        <InvitationMessageInput
          idPrefix="officer-invitation"
          message={invitationMessage}
          onChange={(updatedMessage?: InvitationMessage, error?: string) => {
            setInvitationMessage(updatedMessage);
            updateFormError("message", error);

            if (updatedMessage && !error) {
              buildInvitation({
                ...getInvitationParamsFromState(),
                message: updatedMessage,
              });
            }
          }}
        />
        <LoadingButton
          id="officer-invitation-submit-button"
          color="primary"
          disabled={sending || !formIsValid()}
          loading={sending}
          onClick={sendInvitation}
          startIcon={<SendIcon />}
          variant="contained"
        >
          Send Invite
        </LoadingButton>
      </OfficerInvitationFields>
    </OfficerInvitationFormContainer>
  );
}
